import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { StockTableLayout, Statistic, LockIcon } from '../../components/StockTable';
import { syncApiService } from '../../services/syncApi';
import { TickerDisplayData } from '../../types/ticker';
import { tickerApi } from '../../services/tickerApi';
import { userTickerApi } from '../../services/userTickerApi';
import { userTickerListApi, UserTickerList } from '../../services/userTickerListApi';
import { userApi } from '../../services/userApi';
import { usageLimitsApi } from '../../services/usageLimitsApi';
import CategoryInfoModal from '../../components/CategoryInfoModal';
import TabBar from '../../components/Common/TabBar';
import DeleteListConfirmationModal from '../../components/Modals/DeleteListConfirmationModal';
import ErrorDialog from '../../components/Modals/ErrorDialog';
import ShareListModal from '../../components/ShareListModal';
import Footer from '../../components/Footer';
import Header from '../../components/Header/Header';
import PublicListPromoBar from '../../components/PublicListPromoBar';
import { GoldenCrossIcon, DeathCrossIcon, SupportIcon, ResistanceIcon } from '../../components/Icons';
import { TECHNICAL_STATISTICS, CATEGORY_HEADERS, TickerTechnicalData, MovingAverageData } from '../../utils/technicalsTabUtils';
import { formatTechnicalsStatisticValue, getTechnicalsStatisticColor } from '../../hooks/useTechnicalsTabStatistics';
import { 
  cacheUserLists, 
  getCachedUserLists, 
  clearUserListsCache,
  clearListCache,
  setCacheInvalidation,
  cacheCurrentListName,
  getCachedCurrentListName,
  clearCurrentListNameCache,
  cacheCurrentListId,
  getCachedCurrentListId,
  clearCurrentListIdCache,
  generateUniqueListName
} from '../../utils/listCache';
import { sessionSaveService } from '../../services/sessionSaveService';
import { useEditMode } from '../../hooks/useEditMode';
import { useDragAndDrop } from '../../hooks/useDragAndDrop';
import { useModals } from '../../hooks/useModals';
import { useUserListManagement } from '../../hooks/useUserListManagement';
import { useListPreferences } from '../../hooks/useListPreferences';
import { useAddTicker } from '../../hooks/useAddTicker';
import { publicListApi, PublicListTickerMapping, PublicListPreferences } from '../../services/publicListApi';

// Default tickers to display
const DEFAULT_TICKERS = ['NVDA', 'AAPL', 'GOOGL', 'MSFT', 'AMZN', 'META', 'TSLA'];

const ListTechnicals: React.FC = () => {
  // Set page title
  useEffect(() => {
    document.title = 'Stock Table - Technicals';
  }, []);
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user } = useAuth();
  
  // Pro-only statistics (always show PRO tag, regardless of subscription status)
  const PRO_ONLY_STATISTICS = [
    'SMA_W200',
    'MACD_LINE',
    'MACD_SIGNAL',
    'FIB_0_236',
    'FIB_0_382',
    'FIB_0_5',
    'FIB_0_618',
    'FIB_0_786',
  ];
  
  // Check for public_uuid query parameter
  const searchParams = new URLSearchParams(location.search);
  const publicUuid = searchParams.get('public_uuid');
  const isPublicList = !!publicUuid;
  
  // Public list state
  const [publicListData, setPublicListData] = useState<{
    publicListName: string | null;
    tickerMappings: PublicListTickerMapping[];
    listPreferences: PublicListPreferences | null;
    ownerHasProSubscription: boolean;
  } | null>(null);
  
  // Determine locked statistics based on pro subscription status (for actually locking values)
  // For public lists, use owner's subscription status instead of viewer's
  const lockedStatistics = React.useMemo(() => {
    if (isPublicList && publicListData) {
      return publicListData.ownerHasProSubscription ? [] : PRO_ONLY_STATISTICS;
    }
    return (!isAuthenticated || !user?.hasProSubscription) ? PRO_ONLY_STATISTICS : [];
  }, [isPublicList, publicListData, isAuthenticated, user?.hasProSubscription]);
  const [isLoadingPublicList, setIsLoadingPublicList] = useState(false);
  const [publicListError, setPublicListError] = useState<string | null>(null);
  const [isPublicListNotFound, setIsPublicListNotFound] = useState(false);
  
  // UI state
  const [isEditingTickers, setIsEditingTickers] = useState(false);
  const [tickerList, setTickerList] = useState<string[]>(() => {
    // For public lists, start with empty list - will be populated from public list data
    if (isPublicList) {
      return [];
    }
    // For authenticated users, start with empty list to avoid default ticker API calls
    if (isAuthenticated) {
      return [];
    }
    // For unauthenticated users, only use session storage if it exists, otherwise start empty
    // Default tickers should only be shown on the Landing page
    const savedTickers = sessionStorage.getItem('stocktable-ticker-list');
    return savedTickers ? JSON.parse(savedTickers) : [];
  });
  const [listName, setListName] = useState<string>(() => {
    // For public lists, check sessionStorage for cached public list name
    if (isPublicList && publicUuid) {
      const cachedPublicListName = sessionStorage.getItem(`public-list-name-${publicUuid}`);
      if (cachedPublicListName) {
        return cachedPublicListName;
      }
      // Return empty string if no cached name, will be set when data loads
      return '';
    }
    
    // For authenticated users, try to get cached list name first
    if (isAuthenticated) {
      const cachedListName = getCachedCurrentListName();
      if (cachedListName) {
        return cachedListName;
      }
    }
    
    // Fallback to session storage or default
    const savedListName = sessionStorage.getItem('stocktable-list-name');
    return savedListName || 'Main List';
  });
  
  // Refs to track when we need to reload data
  const refreshUsageLimitsRef = useRef<(() => void) | null>(null);
  
  // Share modal state
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  // Store original session storage values to restore when leaving public list
  const originalSessionStorageRef = useRef<{
    tickerList: string | null;
    listName: string | null;
    hiddenStatistics: string | null;
  } | null>(null);

  // Track if we've attempted to fetch the public list (to prevent infinite retries)
  const publicListFetchAttemptedRef = useRef<string | null>(null);
  
  // Fetch public list data when publicUuid is present
  useEffect(() => {
    // Only fetch if we haven't already attempted for this UUID, or if it's a different UUID
    const shouldFetch = publicUuid && 
                       !publicListData && 
                       !isLoadingPublicList && 
                       publicListFetchAttemptedRef.current !== publicUuid;
    
    if (shouldFetch) {
      // Save original session storage values before loading public list (only for unauthenticated users)
      if (!isAuthenticated && !originalSessionStorageRef.current) {
        originalSessionStorageRef.current = {
          tickerList: sessionStorage.getItem('stocktable-ticker-list'),
          listName: sessionStorage.getItem('stocktable-list-name'),
          hiddenStatistics: sessionStorage.getItem('stocktable-hidden-statistics'),
        };
      }

      // Mark that we're attempting to fetch this UUID
      publicListFetchAttemptedRef.current = publicUuid;
      setIsLoadingPublicList(true);
      setPublicListError(null);
      
      publicListApi.getPublicList(publicUuid)
        .then((response) => {
          setPublicListData({
            publicListName: response.data.public_list_name,
            tickerMappings: response.data.ticker_mappings,
            listPreferences: response.data.list_preferences,
            ownerHasProSubscription: response.data.owner_has_pro_subscription,
          });
          
          // Update ticker list from mappings
          const tickers = response.data.ticker_mappings
            .sort((a, b) => a.order_num - b.order_num)
            .map(mapping => mapping.ticker_symbol);
          setTickerList(tickers);
          
          // Update list name and cache it for tab switching
          const publicListName = response.data.public_list_name || 'Public List';
          setListName(publicListName);
          // Cache the public list name in sessionStorage for immediate restoration on tab switch
          if (publicUuid) {
            sessionStorage.setItem(`public-list-name-${publicUuid}`, publicListName);
          }
        })
        .catch((error) => {
          console.error('Failed to fetch public list:', error);
          const errorMessage = error.message || 'Failed to load public list';
          setPublicListError(errorMessage);
          
          // For 404 or 403 errors, set flag to show not found/not shared message instead of table
          if ((error as any).status === 404 || errorMessage.includes('not found')) {
            setIsPublicListNotFound(true);
            setTickerList([]);
            setListName('Public List');
            setPublicListError('This list does not exist or is no longer available.');
          } else if ((error as any).status === 403 || errorMessage.includes('not publicly shared')) {
            setIsPublicListNotFound(true);
            setTickerList([]);
            setListName('Public List');
            setPublicListError('This list is not publicly shared.');
          }
        })
        .finally(() => {
          setIsLoadingPublicList(false);
        });
    }
    
    // Reset the attempted ref and not found flag when publicUuid changes or becomes null
    if (!publicUuid) {
      publicListFetchAttemptedRef.current = null;
      setIsPublicListNotFound(false);
    }
  }, [publicUuid, publicListData, isLoadingPublicList, isAuthenticated]);

  // Restore session storage on component unmount if we were viewing a public list
  useEffect(() => {
    return () => {
      // Cleanup: restore original session storage when component unmounts if we were in public list mode
      if (originalSessionStorageRef.current && !isAuthenticated) {
        const original = originalSessionStorageRef.current;
        // Restore synchronously before component unmounts
        if (original.tickerList !== null) {
          sessionStorage.setItem('stocktable-ticker-list', original.tickerList);
        } else {
          sessionStorage.removeItem('stocktable-ticker-list');
        }
        if (original.listName !== null) {
          sessionStorage.setItem('stocktable-list-name', original.listName);
        } else {
          sessionStorage.removeItem('stocktable-list-name');
        }
        if (original.hiddenStatistics !== null) {
          sessionStorage.setItem('stocktable-hidden-statistics', original.hiddenStatistics);
        } else {
          sessionStorage.removeItem('stocktable-hidden-statistics');
        }
        originalSessionStorageRef.current = null;
      }
    };
  }, [isAuthenticated]);

  // Reset not found flag and clear error when publicUuid becomes null (e.g., when clicking logo)
  useEffect(() => {
    if (!publicUuid) {
      setIsPublicListNotFound(false);
      setPublicListError(null);
      setPublicListData(null);
    }
  }, [publicUuid]);

  // Also restore when location changes away from public list (before unmount)
  // Watch location.search directly to catch URL changes even when component doesn't unmount
  useEffect(() => {
    // If we're no longer in public list mode, restore from session storage
    if (!publicUuid && !isAuthenticated) {
      // If we have stored original values, restore them
      if (originalSessionStorageRef.current) {
        const original = originalSessionStorageRef.current;
        
        // Restore session storage
        if (original.tickerList !== null) {
          sessionStorage.setItem('stocktable-ticker-list', original.tickerList);
        } else {
          sessionStorage.removeItem('stocktable-ticker-list');
        }
        if (original.listName !== null) {
          sessionStorage.setItem('stocktable-list-name', original.listName);
        } else {
          sessionStorage.removeItem('stocktable-list-name');
        }
        if (original.hiddenStatistics !== null) {
          sessionStorage.setItem('stocktable-hidden-statistics', original.hiddenStatistics);
        } else {
          sessionStorage.removeItem('stocktable-hidden-statistics');
        }
        
        // Reset component state to match restored session storage
        if (original.tickerList !== null) {
          try {
            const restoredTickers = JSON.parse(original.tickerList);
            setTickerList(restoredTickers);
          } catch (e) {
            // If parsing fails, start with empty list (default tickers only on Landing page)
            setTickerList([]);
          }
        } else {
          // Start with empty list if no original data (default tickers only on Landing page)
          setTickerList([]);
        }
        
        if (original.listName !== null) {
          setListName(original.listName);
        } else {
          const savedListName = sessionStorage.getItem('stocktable-list-name');
          setListName(savedListName || 'Main List');
        }
        
        originalSessionStorageRef.current = null;
      } else {
        // If we don't have original values saved, restore from current session storage
        const savedTickers = sessionStorage.getItem('stocktable-ticker-list');
        if (savedTickers) {
          try {
            const restoredTickers = JSON.parse(savedTickers);
            setTickerList(restoredTickers);
          } catch (e) {
            // If parsing fails, start with empty list (default tickers only on Landing page)
            setTickerList([]);
          }
        } else {
          // Start with empty list if no session storage (default tickers only on Landing page)
          setTickerList([]);
        }
        
        const savedListName = sessionStorage.getItem('stocktable-list-name');
        setListName(savedListName || 'Main List');
      }
      
      // Clear public list data
      setPublicListData(null);
      setPublicListError(null);
      setIsPublicListNotFound(false);
    }
  }, [publicUuid, location.search, isAuthenticated]);

  // User list management hook (only for non-public lists)
  const {
    userLists,
    currentListId,
    isLoadingUserLists,
    setUserLists,
    setCurrentListId,
    needsUserListsReload,
    needsListDataReload,
    handleListChange,
    handleCreateNewList,
    handleCreateNewListWithName,
  } = useUserListManagement({
    isAuthenticated: isAuthenticated && !isPublicList,
    tickerList,
    listName,
    setTickerList,
    setListName,
    refreshUsageLimitsRef,
    returnPath: '/technicals',
  });

  // Determine which lists are locked (beyond 3rd position for free users)
  // Free users can only access the first 3 lists (sorted alphabetically)
  const sortedUserLists = useMemo(() => {
    return [...userLists].sort((a, b) => {
      const nameA = a.list_name.toLowerCase();
      const nameB = b.list_name.toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [userLists]);

  const lockedListIds = useMemo(() => {
    if (isAuthenticated && !user?.hasProSubscription && sortedUserLists.length > 3) {
      // Lists beyond the 3rd position (index 3+) are locked
      return sortedUserLists.slice(3).map(list => list.id);
    }
    return [];
  }, [sortedUserLists, isAuthenticated, user?.hasProSubscription]);

  // Check if current list is locked
  const isCurrentListLocked = useMemo(() => {
    return currentListId !== null && lockedListIds.includes(currentListId);
  }, [currentListId, lockedListIds]);

  // List preferences hook (only for non-public lists)
  const {
    hiddenStatistics: privateHiddenStatistics,
    setHiddenStatistics,
    handleHideStatistic,
    handleShowStatistic,
    savePreferences,
  } = useListPreferences({
    isAuthenticated: isAuthenticated && !isPublicList,
    currentListId: isPublicList ? null : currentListId,
    pageType: 'technicals',
    persistToSessionStorageImmediately: true, // Match previous behavior
  });

  // Use public list preferences if in public list mode, otherwise use private preferences
  const hiddenStatistics = isPublicList && publicListData?.listPreferences?.technical_indicators_to_hide
    ? publicListData.listPreferences.technical_indicators_to_hide
    : privateHiddenStatistics;
  
  // Technical data state
  const [tickerTechnicalData, setTickerTechnicalData] = useState<TickerTechnicalData>({});
  const [tickerNames, setTickerNames] = useState<{ [symbol: string]: string }>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  // Initialize syncingTickers from sessionStorage to restore state after refresh
  // Note: currentListId is not available yet at initialization, so we use global key initially
  // The helper function will use list ID-specific key when actually reading/writing
  const [syncingTickers, setSyncingTickers] = useState<Set<string>>(() => {
    const saved = sessionStorage.getItem('stocktable-syncing-tickers');
    return saved ? new Set(JSON.parse(saved)) : new Set();
  });
  const [unsyncedTickers, setUnsyncedTickers] = useState<Set<string>>(new Set());
  const [pollingLimitReached, setPollingLimitReached] = useState<Set<string>>(new Set());
  
  // Refs for polling management to prevent stale closures and race conditions
  const pollingStoppedRef = useRef(false);
  const syncPollingIntervalRef = useRef<NodeJS.Timeout | null>(null); // Use ref for synchronous access
  const isCheckingSyncRef = useRef(false); // Flag to prevent concurrent sync checks
  const currentAttemptRef = useRef(1); // Track current attempt number for interval callback
  const hasRestoredSyncingStateRef = useRef(false); // Flag to track if we've restored syncing state on initial load
  const hasRunInitialDataFetchRef = useRef(false); // Flag to track if initial data fetch has run
  // Ref to prevent recoverPendingTickers from running multiple times
  const hasRecoveredPendingTickersRef = useRef(false);
  
  // Modal state
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('');

  // Auto-save function for authenticated users (needed for handleSaveChanges)
  const autoSaveList = useCallback(async (tickers: string[]) => {
    // Don't auto-save in public list mode
    if (isPublicList || !isAuthenticated) return;
    
    // Determine which list ID to use
    let listId = currentListId;
    
    // If currentListId is null, try to get it from userLists or wait for it
    if (listId === null) {
      // First, try to get from cache (might be set by createDefaultMainListIfNeeded)
      const { getCachedCurrentListId } = await import('../../utils/listCache');
      const cachedListId = getCachedCurrentListId();
      if (cachedListId) {
        console.log('💾 Using cached list ID for auto-save:', cachedListId);
        listId = cachedListId;
      } else if (userLists.length > 0) {
        // If userLists has items, use the first list (or try to get default list)
        // Try to get the user's default list
        try {
          const defaultListResponse = await userApi.getDefaultList();
          if (defaultListResponse.defaultUserTickerListId) {
            const defaultListExists = userLists.some(list => list.id === defaultListResponse.defaultUserTickerListId);
            if (defaultListExists) {
              listId = defaultListResponse.defaultUserTickerListId;
            } else {
              // Default list doesn't exist in userLists, use first list
              listId = userLists[0].id;
            }
          } else {
            // No default list set, use first list
            listId = userLists[0].id;
          }
        } catch (error) {
          // If we can't get default list, use first list
          console.log('⚠️ Could not get default list, using first list:', error);
          listId = userLists[0].id;
        }
      } else {
        // No lists available yet - ticker is saved to sessionStorage and will be recovered when lists load
        console.log('⚠️ Auto-save skipped: currentListId is null and no userLists available. Ticker saved to sessionStorage.');
        return;
      }
    }
    
    // Ensure listId is not null before proceeding
    if (!listId) {
      console.log('⚠️ Auto-save skipped: No list ID available');
      return;
    }
    
    try {
      console.log('💾 Auto-saving list for authenticated user:', { listId, tickers });
      const tickerMappings = tickers.map((ticker, index) => ({
        ticker_symbol: ticker,
        order_num: index + 1
      }));
      
      await userTickerListApi.updateTickerMappings(listId, tickerMappings);
      console.log('✅ Auto-save successful');
      
      // Clear the cache for this list to force reload on tab switch
      clearListCache(listId);
      console.log('🗑️ Cleared cache for list', listId);
      
      // Mark that we need to reload list data to ensure fresh data is loaded
      needsListDataReload.current = true;
      console.log('🔄 Marked list data for reload');
    } catch (error) {
      console.error('❌ Auto-save failed:', error);
    }
  }, [isAuthenticated, currentListId, isPublicList, userLists]);

  // Helper function to get sessionStorage key based on list ID
  const getTickerListKey = useCallback(() => {
    return isAuthenticated && currentListId !== null 
      ? `stocktable-ticker-list-${currentListId}` 
      : 'stocktable-ticker-list';
  }, [isAuthenticated, currentListId]);

  // Helper function to get syncing tickers sessionStorage key based on list ID
  const getSyncingTickersKey = useCallback(() => {
    return isAuthenticated && currentListId !== null 
      ? `stocktable-syncing-tickers-${currentListId}` 
      : 'stocktable-syncing-tickers';
  }, [isAuthenticated, currentListId]);

  // Handle save changes (used by edit mode hook)
  const handleSaveChanges = useCallback(async (saveTempTickerList: string[], saveTempListName: string) => {
    // Don't allow saving changes in public list mode
    if (isPublicList) {
      return;
    }
    
    // Deduplicate the list before saving
    const newTickerList = Array.from(new Set([...saveTempTickerList]));

    // For unauthenticated users, update sessionStorage first so recovery doesn't see stale data.
    // For authenticated users the server is the source of truth; we do not write ticker list to sessionStorage.
    if (!isAuthenticated) {
      sessionStorage.setItem(getTickerListKey(), JSON.stringify(newTickerList));
    }

    setTickerList(newTickerList);
    setListName(saveTempListName);
    setIsEditingTickers(false);
    
    // Auto-save for authenticated users
    if (isAuthenticated && currentListId) {
      try {
        // Update list name if it changed
        if (saveTempListName !== listName) {
          console.log('📝 Updating list name:', { listId: currentListId, oldName: listName, newName: saveTempListName });
          await userTickerListApi.updateListName(currentListId, saveTempListName);
          console.log('✅ List name updated successfully');
          
          // Update the user lists cache to reflect the name change
          setUserLists((prev: UserTickerList[]) => prev.map((list: UserTickerList) => 
            list.id === currentListId 
              ? { ...list, list_name: saveTempListName }
              : list
          ));
          
          // Cache the updated list name to prevent flickering on tab switch
          cacheCurrentListName(saveTempListName);
          console.log('🗑️ Cached updated list name to prevent flickering');
        }
        
        // Update ticker mappings
        await autoSaveList(newTickerList);
        
        // Refresh usage limits after successful mappings update
        try {
          await Promise.all([
            usageLimitsApi.checkTickerLimit(),
            usageLimitsApi.checkListLimit()
          ]);
          console.log('✅ Usage limits refreshed after mappings update');
        } catch (error) {
          console.error('Failed to refresh usage limits:', error);
        }
        
        // Save user list preferences for technical indicators
        try {
          await savePreferences(hiddenStatistics);
        } catch (error) {
          console.error('❌ Failed to save technical preferences:', error);
          // Don't throw error to prevent UI from breaking
        }
      } catch (error) {
        console.error('❌ Failed to update list:', error);
      }
    } else {
      // Save list name to session storage for unauthenticated users
      sessionStorage.setItem('stocktable-list-name', saveTempListName);
      // Preferences are already saved via savePreferences in the hook
      await savePreferences(hiddenStatistics);
    }
    
    // Note: sessionStorage is already updated above before state changes
    // This prevents the recovery mechanism from restoring removed tickers
  }, [tickerList, listName, isAuthenticated, currentListId, setTickerList, setListName, setIsEditingTickers, hiddenStatistics, setUserLists, autoSaveList, savePreferences]);

  // Edit mode hook
  const {
    tempTickerList,
    tempListName,
    setTempTickerList,
    setTempListName,
    handleStartEdit,
    handleCancelEdit,
    handleDoneEdit,
    handleSave,
  } = useEditMode({
    tickerList,
    listName,
    setTickerList,
    setListName,
    isEditingTickers,
    setIsEditingTickers,
    returnPath: '/technicals',
    onSaveChanges: handleSaveChanges,
    hiddenStatistics: !isPublicList ? { technicals: privateHiddenStatistics } : undefined,
  });

  // Drag and drop hook (uses 'insert' behavior by default)
  const {
    draggedTicker,
    dragOverTicker,
    droppedTicker,
    handleDragStart,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleDragEnd,
  } = useDragAndDrop({
    tempTickerList,
    setTempTickerList,
    isEditingTickers,
  });

  // Determine which ticker list to use for display
  const displayTickerList = isEditingTickers ? tempTickerList : tickerList;

  // Determine locked tickers (beyond 10th position for free users OR all tickers if list is locked)
  // Free users can only access the first 10 tickers in a list
  // If the list itself is locked, all tickers are locked
  // For public lists, use owner's subscription status instead of viewer's
  // Note: Calculate after tempTickerList is available from useEditMode hook
  const lockedTickers = useMemo(() => {
    if (isCurrentListLocked) {
      // If list is locked, lock all tickers
      return displayTickerList;
    }
    // For public lists, use owner's subscription status
    if (isPublicList && publicListData) {
      return publicListData.ownerHasProSubscription 
        ? [] // Owner has PRO, so no locked tickers
        : displayTickerList.slice(10); // Owner doesn't have PRO, apply same locks
    }
    // For private lists, use viewer's subscription status
    return (!isAuthenticated || !user?.hasProSubscription) 
      ? displayTickerList.slice(10) // Tickers beyond the 10th position (index 10+)
      : [];
  }, [displayTickerList, isAuthenticated, user?.hasProSubscription, isCurrentListLocked, isPublicList, publicListData]);

  // Function to clear interval without setting stopped flag (for cleanup/reset)
  const clearPollingInterval = () => {
    if (syncPollingIntervalRef.current) {
      clearInterval(syncPollingIntervalRef.current);
      syncPollingIntervalRef.current = null;
    }
  };

  // Function to stop polling
  const stopPolling = () => {
    pollingStoppedRef.current = true;
    clearPollingInterval();
  };

  // Centralized function to handle 429 errors
  const handleRateLimitError = () => {
    console.log('Rate limit (429) detected, stopping all polling and API calls immediately');
    setError(`Rate limit reached. Please wait before trying again.`);
    
    // Stop all polling using ref-based function
    stopPolling();
    
    // Clear all sync states
    setSyncingTickers(new Set());
    setUnsyncedTickers(new Set());
    setPollingLimitReached(new Set());
  };

  // Persist ticker list (only for non-authenticated users)
  // Always deduplicate before saving to prevent duplicates
  useEffect(() => {
    if (!isAuthenticated && !isPublicList) {
      const uniqueTickerList = Array.from(new Set(tickerList));
      sessionStorage.setItem('stocktable-ticker-list', JSON.stringify(uniqueTickerList));
    }
  }, [tickerList, isAuthenticated, isPublicList]);

  // Ensure tickerList is always deduplicated (for authenticated users too)
  useEffect(() => {
    if (tickerList.length > 0) {
      const uniqueTickerList = Array.from(new Set(tickerList));
      if (uniqueTickerList.length !== tickerList.length) {
        console.log(`🔧 ListTechnicals: Deduplicating tickerList: ${tickerList.length} -> ${uniqueTickerList.length}`);
        setTickerList(uniqueTickerList);
      }
    }
  }, [tickerList]);



  // Helper function to get ticker data for display
  const getTickerData = (symbol: string): TickerDisplayData | undefined => {
    const tickerData = tickerTechnicalData[symbol];
    const isSyncing = syncingTickers.has(symbol);
    const isUnsynced = unsyncedTickers.has(symbol);
    
    // If ticker is syncing, unsynced, or has no data yet, return a loading state
    if (isSyncing || isUnsynced || !tickerData) {
      return {
        ticker: symbol,
        name: symbol,
        ticker_name: tickerNames[symbol] || symbol,
        latest: 0,
        change: 0,
        changePercent: 0,
        volume: 0,
        high: 0,
        low: 0,
        open: 0,
        updated: Date.now(),
        prevClose: 0,
        isSyncing: isSyncing || isUnsynced,
      };
    }

    return {
      ticker: symbol,
      name: symbol,
      ticker_name: tickerNames[symbol] || symbol,
      latest: tickerData.price?.close_price_adjusted || 0,
      change: 0, // Not available in technical data
      changePercent: 0, // Not available in technical data
      volume: 0, // Not available in technical data
      high: 0, // Not available in technical data
      low: 0, // Not available in technical data
      open: 0, // Not available in technical data
      updated: tickerData.price?.date?.getTime() || Date.now(),
      prevClose: tickerData.price?.close_price_adjusted || 0, // Using current price as prev close
      isSyncing: false,
    };
  };

  // Helper function to format statistic values using shared utility
  const formatStatisticValue = (statisticKey: string, ticker: TickerDisplayData): React.ReactNode => {
    // Check if this statistic is locked for non-pro users BEFORE formatting
    // If the value is null and the statistic is locked, show lock icon
    if (lockedStatistics.includes(statisticKey)) {
      const tickerData = tickerTechnicalData[ticker.ticker];
      if (tickerData) {
        const technicalValue = tickerData.technicals[statisticKey]?.value;
        const nonNumericValue = tickerData.technicals[statisticKey]?.nonNumericValue;
        // If value is null (locked), show lock icon instead of formatting
        if (technicalValue === null || nonNumericValue === null) {
          return <LockIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" onClick={!isPublicList ? () => navigate('/pricing', { state: { fromUpgrade: true } }) : undefined} tooltipText={isPublicList ? "Locked due to the list owner's plan" : "Upgrade to the Pro Plan to see"} />;
        }
      }
    }
    
    return formatTechnicalsStatisticValue(statisticKey, ticker, {
      tickerTechnicalData,
      syncingTickers,
      unsyncedTickers,
      pollingLimitReached,
    });
  };

  // Helper function to get statistic color using shared utility
  const getStatisticColor = (statisticKey: string, ticker: TickerDisplayData): string => {
    return getTechnicalsStatisticColor(statisticKey, ticker, {
      tickerTechnicalData,
    });
  };

  // Function to detect golden cross for a given indicator pair
  const hasGoldenCross = (tickerData: TickerTechnicalData[string], currentIndicator: string, compareIndicator: string): boolean => {
    if (!tickerData || !tickerData.movingAverages || tickerData.movingAverages.length === 0) {
      return false;
    }

    // Get current values (days_ago = 0) from technicals
    const currentValue = tickerData.technicals[currentIndicator]?.value;
    const currentCompareValue = tickerData.technicals[compareIndicator]?.value;

    if (currentValue === undefined || currentValue === null || currentCompareValue === undefined || currentCompareValue === null) {
      return false;
    }

    // Check if current value > compare value (golden cross condition)
    if (currentValue <= currentCompareValue) {
      return false;
    }

    // Check if there was a cross in the historical data (days_ago 1-6)
    return tickerData.movingAverages.some(ma => {
      const historicalValue = ma[currentIndicator.toLowerCase() as keyof MovingAverageData];
      const historicalCompareValue = ma[compareIndicator.toLowerCase() as keyof MovingAverageData];
      
      if (historicalValue === null || historicalValue === undefined || 
          historicalCompareValue === null || historicalCompareValue === undefined) {
        return false;
      }

      // Check if historical value was <= historical compare value (indicating a cross occurred)
      return historicalValue <= historicalCompareValue;
    });
  };

  // Function to detect death cross for a given indicator pair
  const hasDeathCross = (tickerData: TickerTechnicalData[string], currentIndicator: string, compareIndicator: string): boolean => {
    if (!tickerData || !tickerData.movingAverages || tickerData.movingAverages.length === 0) {
      return false;
    }

    // Get current values (days_ago = 0) from technicals
    const currentValue = tickerData.technicals[currentIndicator]?.value;
    const currentCompareValue = tickerData.technicals[compareIndicator]?.value;

    if (currentValue === undefined || currentValue === null || currentCompareValue === undefined || currentCompareValue === null) {
      return false;
    }

    // Check if current value < compare value (death cross condition - reversed from golden cross)
    if (currentValue >= currentCompareValue) {
      return false;
    }

    // Check if there was a cross in the historical data (days_ago 1-6)
    return tickerData.movingAverages.some(ma => {
      const historicalValue = ma[currentIndicator.toLowerCase() as keyof MovingAverageData];
      const historicalCompareValue = ma[compareIndicator.toLowerCase() as keyof MovingAverageData];
      
      if (historicalValue === null || historicalValue === undefined || 
          historicalCompareValue === null || historicalCompareValue === undefined) {
        return false;
      }

      // Check if historical value was >= historical compare value (indicating a cross occurred from above to below)
      return historicalValue >= historicalCompareValue;
    });
  };

  // Helper function to detect if price crossed above a Bollinger Band within past 6 days
  const hasBBGoldenCross = (tickerData: TickerTechnicalData[string], bbKey: string): boolean => {
    if (!tickerData || !tickerData.price) return false;
    
    const currentPrice = tickerData.price.close_price_adjusted;
    const previousPrice = tickerData.price.previous_close_price_adjusted;
    const bbValue = tickerData.technicals[bbKey]?.value;
    
    if (currentPrice === undefined || currentPrice === null || 
        bbValue === undefined || bbValue === null) {
      return false;
    }
    
    // Current price must be above the BB
    if (currentPrice <= bbValue) {
      return false;
    }
    
    // Check if price just crossed above (previous price was below or equal)
    if (previousPrice !== undefined && previousPrice !== null) {
      return previousPrice <= bbValue;
    }
    
    // If no previous price, we can't determine if it just crossed
    // For now, return false - could be enhanced with historical data
    return false;
  };

  // Helper function to detect if price crossed below a Bollinger Band within past 6 days
  const hasBBDeathCross = (tickerData: TickerTechnicalData[string], bbKey: string): boolean => {
    if (!tickerData || !tickerData.price) return false;
    
    const currentPrice = tickerData.price.close_price_adjusted;
    const previousPrice = tickerData.price.previous_close_price_adjusted;
    const bbValue = tickerData.technicals[bbKey]?.value;
    
    if (currentPrice === undefined || currentPrice === null || 
        bbValue === undefined || bbValue === null) {
      return false;
    }
    
    // Current price must be below the BB
    if (currentPrice >= bbValue) {
      return false;
    }
    
    // Check if price just crossed below (previous price was above or equal)
    if (previousPrice !== undefined && previousPrice !== null) {
      return previousPrice >= bbValue;
    }
    
    // If no previous price, we can't determine if it just crossed
    // For now, return false - could be enhanced with historical data
    return false;
  };

  // Helper function to get Bollinger Band icons (golden cross, death cross, support, resistance)
  const getBollingerBandIcon = (tickerData: TickerTechnicalData[string], statisticKey: string): React.ReactNode => {
    if (!tickerData || !tickerData.price) return null;
    
    const currentPrice = tickerData.price.close_price_adjusted;
    const bbValue = tickerData.technicals[statisticKey]?.value;
    
    if (currentPrice === undefined || currentPrice === null || 
        bbValue === undefined || bbValue === null) {
      return null;
    }
    
    // Get all BB values
    const upperBB = tickerData.technicals['BB_20_UPPER_20']?.value;
    const middleBB = tickerData.technicals['BB_20_SMA']?.value;
    const lowerBB = tickerData.technicals['BB_20_LOWER_20']?.value;
    
    if (upperBB === undefined || upperBB === null ||
        middleBB === undefined || middleBB === null ||
        lowerBB === undefined || lowerBB === null) {
      return null;
    }
    
    // Check for golden cross (price crossed above this band)
    if (hasBBGoldenCross(tickerData, statisticKey)) {
      const bandName = statisticKey.includes('UPPER') ? 'Upper' : 
                       statisticKey.includes('SMA') ? 'Middle' : 'Lower';
      return (
        <span className="ml-1 group/golden-cross relative">
          <GoldenCrossIcon className="w-4 h-4" />
          <div className="absolute hidden group-hover/golden-cross:block left-0 top-full mt-1 w-48 bg-white dark:bg-background-card border border-gray-200 dark:border-border-color rounded-md shadow-lg p-2 text-xs text-gray-600 dark:text-text-secondary z-[60] font-sans whitespace-normal break-words">
            Price crossed above {bandName} Bollinger Band within the past 6 days
          </div>
        </span>
      );
    }
    
    // Check for death cross (price crossed below this band)
    if (hasBBDeathCross(tickerData, statisticKey)) {
      const bandName = statisticKey.includes('UPPER') ? 'Upper' : 
                       statisticKey.includes('SMA') ? 'Middle' : 'Lower';
      return (
        <span className="ml-1 group/death-cross relative">
          <DeathCrossIcon className="w-4 h-4" />
          <div className="absolute hidden group-hover/death-cross:block left-0 top-full mt-1 w-48 bg-white dark:bg-background-card border border-gray-200 dark:border-border-color rounded-md shadow-lg p-2 text-xs text-gray-600 dark:text-text-secondary z-[60] font-sans whitespace-normal break-words">
            Price crossed below {bandName} Bollinger Band within the past 6 days
          </div>
        </span>
      );
    }
    
    // Determine the closest lower and higher bands
    let closestLowerBand: string | null = null;
    let closestHigherBand: string | null = null;
    
    // Find closest lower band (largest band value that is still < current price)
    if (currentPrice > lowerBB) {
      if (currentPrice >= middleBB) {
        if (currentPrice >= upperBB) {
          // Price is above all bands - upper is closest lower
          closestLowerBand = 'BB_20_UPPER_20';
        } else {
          // Price is between middle and upper - middle is closest lower
          closestLowerBand = 'BB_20_SMA';
        }
      } else {
        // Price is between lower and middle - lower is closest lower
        closestLowerBand = 'BB_20_LOWER_20';
      }
    }
    
    // Find closest higher band (smallest band value that is still > current price)
    if (currentPrice < upperBB) {
      if (currentPrice < middleBB) {
        if (currentPrice < lowerBB) {
          // Price is below all bands - lower is closest higher
          closestHigherBand = 'BB_20_LOWER_20';
        } else {
          // Price is between lower and middle - middle is closest higher
          closestHigherBand = 'BB_20_SMA';
        }
      } else {
        // Price is between middle and upper - upper is closest higher
        closestHigherBand = 'BB_20_UPPER_20';
      }
    }
    
    // Show support icon only on the closest lower band
    if (closestLowerBand === statisticKey) {
      const bandName = statisticKey.includes('UPPER') ? 'Upper' : 
                       statisticKey.includes('SMA') ? 'Middle' : 'Lower';
      return (
        <span className="ml-1 group/support relative">
          <SupportIcon className="w-4 h-4" />
          <div className="absolute hidden group-hover/support:block left-0 top-full mt-1 w-48 bg-white dark:bg-background-card border border-gray-200 dark:border-border-color rounded-md shadow-lg p-2 text-xs text-gray-600 dark:text-text-secondary z-[60] font-sans whitespace-normal break-words">
            Price is above {bandName} Bollinger Band (support level)
          </div>
        </span>
      );
    }
    
    // Show resistance icon only on the closest higher band
    if (closestHigherBand === statisticKey) {
      const bandName = statisticKey.includes('UPPER') ? 'Upper' : 
                       statisticKey.includes('SMA') ? 'Middle' : 'Lower';
      return (
        <span className="ml-1 group/resistance relative">
          <ResistanceIcon className="w-4 h-4" />
          <div className="absolute hidden group-hover/resistance:block left-0 top-full mt-1 w-48 bg-white dark:bg-background-card border border-gray-200 dark:border-border-color rounded-md shadow-lg p-2 text-xs text-gray-600 dark:text-text-secondary z-[60] font-sans whitespace-normal break-words">
            Price is below {bandName} Bollinger Band (resistance level)
          </div>
        </span>
      );
    }
    
    return null;
  };

  // Helper function to determine which Fibonacci icon to show for a given level
  const getFibonacciIcon = (tickerData: TickerTechnicalData[string], statisticKey: string): React.ReactNode => {
    if (!tickerData) return null;

    // Get swing direction
    const swingDirection = tickerData.technicals['FIB_SWING_DIRECTION']?.nonNumericValue;
    if (!swingDirection || (swingDirection !== 'up' && swingDirection !== 'down')) {
      return null;
    }

    // Get last closing price
    const lastClosingPrice = tickerData.price?.close_price_adjusted;
    if (lastClosingPrice === undefined || lastClosingPrice === null) {
      return null;
    }

    // Get current Fibonacci level value
    const currentLevelValue = tickerData.technicals[statisticKey]?.value;
    if (currentLevelValue === undefined || currentLevelValue === null) {
      return null;
    }

    // Get all Fibonacci retracement levels
    const fibLevels = ['FIB_0_0', 'FIB_0_236', 'FIB_0_382', 'FIB_0_5', 'FIB_0_618', 'FIB_0_786', 'FIB_1_0'];
    const fibValues: { [key: string]: number } = {};
    
    for (const level of fibLevels) {
      const value = tickerData.technicals[level]?.value;
      if (value !== undefined && value !== null) {
        fibValues[level] = value;
      }
    }

    if (swingDirection === 'up') {
      // For up swing:
      // - Death cross icon: retracement level with lowest value that is higher than last closing price
      // - Support icon: retracement level with highest value that is lower than last closing price
      
      // Find levels higher than closing price
      const levelsAbove = Object.entries(fibValues)
        .filter(([_, value]) => value > lastClosingPrice)
        .sort(([_, a], [__, b]) => a - b); // Sort by value ascending
      
      // Find levels lower than closing price
      const levelsBelow = Object.entries(fibValues)
        .filter(([_, value]) => value < lastClosingPrice)
        .sort(([_, a], [__, b]) => b - a); // Sort by value descending

      // Show death cross icon if this is the lowest level above closing price
      if (levelsAbove.length > 0 && levelsAbove[0][0] === statisticKey) {
        return (
          <span className="ml-1 group/death-cross relative">
            <DeathCrossIcon className="w-4 h-4" />
            <div className="absolute hidden group-hover/death-cross:block left-0 top-full mt-1 w-48 bg-white dark:bg-background-card border border-gray-200 dark:border-border-color rounded-md shadow-lg p-2 text-xs text-gray-600 dark:text-text-secondary z-[60] font-sans whitespace-normal break-words">
              Last closing price broke below this support level
            </div>
          </span>
        );
      }

      // Show support icon if this is the highest level below closing price
      if (levelsBelow.length > 0 && levelsBelow[0][0] === statisticKey) {
        return (
          <span className="ml-1 group/support relative">
            <SupportIcon className="w-4 h-4" />
            <div className="absolute hidden group-hover/support:block left-0 top-full mt-1 w-48 bg-white dark:bg-background-card border border-gray-200 dark:border-border-color rounded-md shadow-lg p-2 text-xs text-gray-600 dark:text-text-secondary z-[60] font-sans whitespace-normal break-words">
              Next support level
            </div>
          </span>
        );
      }
    } else {
      // For down swing:
      // - Golden cross icon: retracement level with highest value that is lower than last closing price
      // - Resistance icon: retracement level with lowest value that is higher than last closing price
      
      // Find levels lower than closing price
      const levelsBelow = Object.entries(fibValues)
        .filter(([_, value]) => value < lastClosingPrice)
        .sort(([_, a], [__, b]) => b - a); // Sort by value descending
      
      // Find levels higher than closing price
      const levelsAbove = Object.entries(fibValues)
        .filter(([_, value]) => value > lastClosingPrice)
        .sort(([_, a], [__, b]) => a - b); // Sort by value ascending

      // Show golden cross icon if this is the highest level below closing price
      if (levelsBelow.length > 0 && levelsBelow[0][0] === statisticKey) {
        return (
          <span className="ml-1 group/golden-cross relative">
            <GoldenCrossIcon className="w-4 h-4" />
            <div className="absolute hidden group-hover/golden-cross:block left-0 top-full mt-1 w-48 bg-white dark:bg-background-card border border-gray-200 dark:border-border-color rounded-md shadow-lg p-2 text-xs text-gray-600 dark:text-text-secondary z-[60] font-sans whitespace-normal break-words">
              Last closing price broke above this resistance level
            </div>
          </span>
        );
      }

      // Show resistance icon if this is the lowest level above closing price
      if (levelsAbove.length > 0 && levelsAbove[0][0] === statisticKey) {
        return (
          <span className="ml-1 group/resistance relative">
            <ResistanceIcon className="w-4 h-4" />
            <div className="absolute hidden group-hover/resistance:block left-0 top-full mt-1 w-48 bg-white dark:bg-background-card border border-gray-200 dark:border-border-color rounded-md shadow-lg p-2 text-xs text-gray-600 dark:text-text-secondary z-[60] font-sans whitespace-normal break-words">
              Next resistance level
            </div>
          </span>
        );
      }
    }

    return null;
  };

  // Function to fetch technical data for a list of synced tickers
  const fetchTechnicalData = async (syncedTickers: any[]): Promise<TickerTechnicalData> => {
    if (syncedTickers.length === 0) {
      return {};
    }

    try {
      const tickerBasicsIds = syncedTickers.map((ticker: any) => ticker.ticker_basics_id);
      
      // Add small delay to avoid rate limiting
      await new Promise(resolve => setTimeout(resolve, 200));
      
      const api = isAuthenticated ? userTickerApi : tickerApi;
      const response = await api.getTickerTechnical({
        tickerBasicsIds: tickerBasicsIds,
        daysAgo: 0, // Get latest data
        ownerHasProSubscription: isPublicList && publicListData ? publicListData.ownerHasProSubscription : undefined,
      });

      const newTechnicalData: TickerTechnicalData = {};
      
      response.data.forEach((tickerData: any) => {
        const symbol = tickerData.tickerSymbol;
        
        newTechnicalData[symbol] = {
          technicals: {},
          price: null, // Will be populated by price API
          movingAverages: [],
        };

        // Process technical indicators
        if (tickerData.indicators) {
          tickerData.indicators.forEach((indicator: any) => {
            newTechnicalData[symbol].technicals[indicator.indicatorKey] = {
              ticker_basics_id: tickerData.tickerBasicsId,
              indicator_key: indicator.indicatorKey,
              value: indicator.indicatorValue,
              nonNumericValue: indicator.indicatorNonNumericValue || indicator.indicator_non_numeric_value || null,
              date: indicator.date,
            };
          });
        }
      });

      setTickerTechnicalData(prev => {
        const updated = { ...prev, ...newTechnicalData };
        return updated;
      });
      
      return newTechnicalData;
    } catch (error) {
      console.error('Error fetching technical data:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      
      // Check if it's a rate limit error (429)
      if (errorMessage.includes('429') || errorMessage.includes('rate limit')) {
        handleRateLimitError();
      } else {
        setError(`Failed to fetch technical data: ${errorMessage}`);
      }
      return {};
    }
  };

  // Function to fetch moving average cross data for a list of synced tickers
  const fetchMovingAverageData = async (syncedTickers: any[]): Promise<{ [tickerSymbol: string]: MovingAverageData[] }> => {
    if (syncedTickers.length === 0) {
      return {};
    }

    try {
      const tickerBasicsIds = syncedTickers.map((ticker: any) => ticker.ticker_basics_id);
      
      const api = isAuthenticated ? userTickerApi : tickerApi;
      const response = await api.getMovingAverageCrossData({
        ticker_basics_ids: tickerBasicsIds,
        start_days_ago: 1,
        end_days_ago: 6,
      });

      const movingAverageData: { [tickerSymbol: string]: MovingAverageData[] } = {};
      
      response.data.tickers.forEach((tickerData: any) => {
        const symbol = tickerData.ticker_symbol;
        movingAverageData[symbol] = tickerData.moving_averages.map((ma: any) => ({
          days_ago: ma.days_ago,
          date: new Date(ma.date),
          sma_20: ma.sma_20,
          sma_50: ma.sma_50,
          sma_200: ma.sma_200,
          sma_w50: ma.sma_w50,
          sma_w200: ma.sma_w200,
          ema_21: ma.ema_21,
          ema_50: ma.ema_50,
          ema_200: ma.ema_200,
        }));
      });

      return movingAverageData;
    } catch (error) {
      console.error('Error fetching moving average data:', error);
      return {};
    }
  };

  // Function to fetch both technical and moving average data in parallel
  const fetchAllTechnicalData = async (syncedTickers: any[]): Promise<TickerTechnicalData> => {
    if (syncedTickers.length === 0) {
      return {};
    }

    try {
      // Call both APIs in parallel
      const [technicalData, movingAverageData] = await Promise.all([
        fetchTechnicalData(syncedTickers),
        fetchMovingAverageData(syncedTickers)
      ]);

      // Merge moving average data into technical data
      Object.keys(technicalData).forEach(symbol => {
        if (movingAverageData[symbol]) {
          technicalData[symbol].movingAverages = movingAverageData[symbol];
        }
      });

      return technicalData;
    } catch (error) {
      console.error('Error fetching all technical data:', error);
      throw error;
    }
  };

  // Function to fetch price data for a list of tickers
  const fetchPriceData = async (tickers: string[], currentTechnicalData: TickerTechnicalData) => {
    if (tickers.length === 0) return;

    try {
      const api = isAuthenticated ? userTickerApi : tickerApi;
      const response = await api.getTickerLatestPrice({
        ticker_symbols: tickers,
      });

      // Merge with existing data instead of replacing it
      setTickerTechnicalData(prev => {
        const updatedTechnicalData = { ...prev };
        
        response.data.tickers.forEach((priceData: any) => {
          const symbol = priceData.ticker_symbol;
          if (updatedTechnicalData[symbol] && priceData.prices && priceData.prices.length > 0) {
            // Use the first (most recent) price from the array
            const latestPrice = priceData.prices[0];
            const previousPrice = priceData.prices.length > 1 ? priceData.prices[1] : null;
            
            updatedTechnicalData[symbol].price = {
              ticker_symbol: priceData.ticker_symbol,
              ticker_basics_id: priceData.ticker_basics_id,
              close_price_adjusted: latestPrice.close_price_adjusted,
              date: new Date(latestPrice.date),
              previous_close_price_adjusted: previousPrice ? previousPrice.close_price_adjusted : null,
              previous_date: previousPrice ? new Date(previousPrice.date) : null,
            };
          }
        });
        
        return updatedTechnicalData;
      });
    } catch (error) {
      console.error('Error fetching price data:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      
      // Check if it's a rate limit error (429)
      if (errorMessage.includes('429') || errorMessage.includes('rate limit')) {
        handleRateLimitError();
      } else {
        setError(`Failed to fetch price data: ${errorMessage}`);
      }
    }
  };

  // Function to check sync status and fetch data for specific tickers
  const checkSyncAndFetchDataForTickers = async (tickersToCheck: string[], attemptNumber: number = 1) => {
    console.log('ListTechnicals: checkSyncAndFetchDataForTickers called with:', tickersToCheck, `(attempt ${attemptNumber})`);
    
    if (tickersToCheck.length === 0) {
      console.log('ListTechnicals: No tickers to check, returning early');
      return;
    }

    const MAX_SYNC_POLL_ATTEMPTS = 5;

    // Guard: Check if we've exceeded the attempt limit before proceeding
    if (attemptNumber > MAX_SYNC_POLL_ATTEMPTS) {
      console.warn(`ListTechnicals: Attempt ${attemptNumber} exceeds limit (${MAX_SYNC_POLL_ATTEMPTS}), stopping`);
      stopPolling();
      setPollingLimitReached(prev => {
        const newSet = new Set(prev);
        tickersToCheck.forEach(ticker => newSet.add(ticker));
        return newSet;
      });
      isCheckingSyncRef.current = false;
      return;
    }

    // Guard: Don't proceed if polling has been stopped (e.g., after reaching limit or 429 error)
    if (pollingStoppedRef.current) {
      console.log(`ListTechnicals: checkSyncAndFetchDataForTickers called but polling is stopped, ignoring`);
      isCheckingSyncRef.current = false;
      return;
    }

    // Guard: Prevent concurrent execution - if a sync check is already in progress, ignore this call
    if (isCheckingSyncRef.current) {
      console.log(`ListTechnicals: checkSyncAndFetchDataForTickers already in progress, ignoring concurrent call`);
      return;
    }

    isCheckingSyncRef.current = true;

    // Update global polling attempts for display purposes (removed unused state)

    try {
      console.log('🚀 ListTechnicals: About to call checkTickerSyncStatus for:', tickersToCheck);
      const api = isAuthenticated ? userTickerApi : tickerApi;
      const syncResponse = await api.checkTickerSyncStatus({
        ticker_symbols: tickersToCheck,
      });
      console.log('🚀 ListTechnicals: API call completed successfully for:', tickersToCheck);
      console.log('🚀 ListTechnicals: API response:', syncResponse);
      
      // Calculate last updated from latest last_sync_ended_at of synced tickers
      if (syncResponse.success && syncResponse.data && syncResponse.data.tickers) {
        const syncedTickers = syncResponse.data.tickers.filter((t: any) => t.is_synced === true && t.last_sync_ended_at);
        if (syncedTickers.length > 0) {
          const lastSyncDates = syncedTickers
            .map((t: any) => {
              const date = typeof t.last_sync_ended_at === 'string' 
                ? new Date(t.last_sync_ended_at) 
                : t.last_sync_ended_at;
              return date instanceof Date && !isNaN(date.getTime()) ? date : null;
            })
            .filter((d: Date | null): d is Date => d !== null);
          
          if (lastSyncDates.length > 0) {
            const latestDate = new Date(Math.max(...lastSyncDates.map((d: Date) => d.getTime())));
            setLastUpdated(latestDate);
          }
        }
      }

      // Check if response is valid before processing (same as ListAnalystViews)
      if (!syncResponse.success || !syncResponse.data || !syncResponse.data.tickers) {
        console.error('ListTechnicals: Invalid sync response structure:', syncResponse);
        setError('Invalid response from sync status check');
        isCheckingSyncRef.current = false;
        return;
      }

      // Store ticker names from sync response
      const newTickerNames: { [symbol: string]: string } = {};
      syncResponse.data.tickers.forEach((ticker: any) => {
        if (ticker.ticker_name) {
          newTickerNames[ticker.ticker_symbol] = ticker.ticker_name;
        }
      });
      setTickerNames(prev => ({ ...prev, ...newTickerNames }));

      const newlySyncedTickers = syncResponse.data.tickers
        .filter((ticker: any) => ticker.is_synced === true)
        .map((ticker: any) => ticker.ticker_symbol);
      
      const stillUnsyncedTickers = syncResponse.data.tickers
        .filter((ticker: any) => ticker.is_synced === false)
        .map((ticker: any) => ticker.ticker_symbol);

      console.log('🚀 ListTechnicals: newlySyncedTickers:', newlySyncedTickers);
      console.log('🚀 ListTechnicals: stillUnsyncedTickers:', stillUnsyncedTickers);
      console.log('🚀 ListTechnicals: stillUnsyncedTickers.length:', stillUnsyncedTickers.length);

      // Update unsynced tickers state
      setUnsyncedTickers(prev => {
        const newSet = new Set(prev);
        stillUnsyncedTickers.forEach((ticker: string) => newSet.add(ticker));
        // Remove newly synced tickers from unsynced set
        newlySyncedTickers.forEach((ticker: string) => newSet.delete(ticker));
        console.log('🚀 ListTechnicals: Updated unsyncedTickers state, new size:', newSet.size, 'tickers:', Array.from(newSet));
        return newSet;
      });

      // Remove newly synced tickers from syncing set so they don't show loading animation
      if (newlySyncedTickers.length > 0) {
        setSyncingTickers(prev => {
          const newSet = new Set(prev);
          newlySyncedTickers.forEach((ticker: string) => newSet.delete(ticker));
          return newSet;
        });
      }

      // Fetch data for newly synced tickers
      if (newlySyncedTickers.length > 0) {
        console.log(`ListTechnicals: Fetching data for newly synced tickers: ${newlySyncedTickers.join(', ')}`);
        
        // Add small delay to avoid rate limiting
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Fetch technical data for newly synced tickers
        const technicalData = await fetchAllTechnicalData(syncResponse.data.tickers.filter((ticker: any) => ticker.is_synced));
        await new Promise(resolve => setTimeout(resolve, 100)); // Small delay between calls
        await fetchPriceData(newlySyncedTickers, technicalData);
        // Safety: ensure UI doesn't remain in "syncing" pulse once data is fetched.
        setSyncingTickers(prev => {
          const next = new Set(prev);
          newlySyncedTickers.forEach((t: string) => next.delete(t));
          return next;
        });
        setUnsyncedTickers(prev => {
          const next = new Set(prev);
          newlySyncedTickers.forEach((t: string) => next.delete(t));
          return next;
        });
        // lastUpdated is already set from last_sync_ended_at above, don't override it
        
        // Save newly synced tickers to database if authenticated
        // Always save when tickers become synced, even if they're already in tickerList,
        // because the database mappings need to be updated now that they have ticker_basics_id
        if (isAuthenticated && currentListId !== null && newlySyncedTickers.length > 0) {
          const mergedList = Array.from(new Set([...newlySyncedTickers, ...tickerList]));
          await autoSaveList(mergedList);
          console.log(`💾 Saved ${newlySyncedTickers.length} newly synced tickers to database during polling:`, newlySyncedTickers);
        }
        
        console.log(`ListTechnicals: Successfully fetched data for ${newlySyncedTickers.join(', ')}`);
      }

      // If there are still unsynced tickers, continue polling
      console.log('🚀 ListTechnicals: Checking if polling should continue. stillUnsyncedTickers.length:', stillUnsyncedTickers.length, 'attemptNumber:', attemptNumber);
      if (stillUnsyncedTickers.length > 0) {
        console.log('🚀 ListTechnicals: Entered stillUnsyncedTickers.length > 0 block');
        // If we've reached the polling limit, stop polling
        if (attemptNumber >= MAX_SYNC_POLL_ATTEMPTS) {
          console.warn(
            `Sync polling limit reached (${MAX_SYNC_POLL_ATTEMPTS} attempts) for tickers: ${stillUnsyncedTickers.join(', ')}. Stopping polling silently.`
          );
          setPollingLimitReached(prev => {
            const newSet = new Set(prev);
            stillUnsyncedTickers.forEach((ticker: string) => newSet.add(ticker));
            return newSet;
          });
          stopPolling();
          isCheckingSyncRef.current = false; // Reset flag
        } else {
          // Double-check we haven't stopped polling before setting up interval
          if (pollingStoppedRef.current) {
            console.log(`ListTechnicals: Polling stopped, not setting up interval`);
            isCheckingSyncRef.current = false; // Reset flag before returning
            return;
          }

          console.log(`Found ${stillUnsyncedTickers.length} still unsynced tickers: ${stillUnsyncedTickers.join(', ')}. Attempt ${attemptNumber}/5.`);
          console.log('🚀 ListTechnicals: About to set up polling interval');
          
          // Use fixed 2-second delay between polling attempts
          const pollingDelay = 2000; // 2 seconds
          
          // Clear any existing interval before creating a new one
          // This ensures we always use the latest attempt number
          clearPollingInterval();
          console.log('🚀 ListTechnicals: Cleared existing polling interval');
          
          console.log(`Starting sync polling with ${pollingDelay}ms delay`);
          const interval = setInterval(() => {
            console.log('🚀 ListTechnicals: Polling interval callback fired');
            // Check if polling was stopped or we've reached the limit
            if (pollingStoppedRef.current) {
              clearInterval(interval);
              syncPollingIntervalRef.current = null;
              return;
            }
            
            // Get the next attempt number from the ref
            const nextAttempt = currentAttemptRef.current + 1;
            
            // Check if we've exceeded the limit
            if (nextAttempt > MAX_SYNC_POLL_ATTEMPTS) {
              console.log(`ListTechnicals: Reached attempt limit (${MAX_SYNC_POLL_ATTEMPTS}), stopping interval`);
              clearInterval(interval);
              syncPollingIntervalRef.current = null;
              pollingStoppedRef.current = true;
              return;
            }
            
            // Update the ref for the next iteration
            currentAttemptRef.current = nextAttempt;
            
            // Check current unsynced tickers from state
            setUnsyncedTickers(currentUnsynced => {
              if (currentUnsynced.size > 0) {
                console.log(`Polling: checking ${currentUnsynced.size} unsynced tickers:`, Array.from(currentUnsynced));
                checkSyncAndFetchDataForTickers(Array.from(currentUnsynced), nextAttempt);
              } else {
                console.log('Polling: no unsynced tickers found, stopping polling');
                clearInterval(interval);
                syncPollingIntervalRef.current = null;
                pollingStoppedRef.current = true;
              }
              return currentUnsynced;
            });
          }, pollingDelay);
          
          // Update the current attempt ref so the interval callback knows the next attempt number
          currentAttemptRef.current = attemptNumber;
          syncPollingIntervalRef.current = interval;
          
          // Reset flag after setting up interval
          isCheckingSyncRef.current = false;
        }
      } else {
        // All tickers synced, stop polling
        console.log('All tickers synced, stopping polling');
        stopPolling();
        isCheckingSyncRef.current = false; // Reset flag
      }

      setError(null);
    } catch (err) {
      console.error('ListTechnicals: Error in checkSyncAndFetchDataForTickers:', err);
      
      // Check if it's a 429 rate limit error
      const isRateLimitError = 
        (axios.isAxiosError(err) && err.response?.status === 429) ||
        (err instanceof Error && (err.message.includes('429') || err.message.includes('rate limit') || err.message.includes('Rate limit')));
      
      if (isRateLimitError) {
        console.warn(`ListTechnicals: Rate limit (429) detected. Stopping polling.`);
        handleRateLimitError();
      } else {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
        setError(`Failed to check sync status: ${errorMessage}`);
        
        // Stop polling on any error
        stopPolling();
      }
      isCheckingSyncRef.current = false; // Reset flag
    }
  };

  // Function to check sync status and fetch data for synced tickers (legacy function)
  const checkSyncAndFetchData = async () => {
    console.log('ListTechnicals: checkSyncAndFetchData called with tickerList:', tickerList);
    if (tickerList.length === 0) {
      console.log('ListTechnicals: tickerList is empty, returning early');
      return;
    }

    // Reset polling state before starting new sync check
    pollingStoppedRef.current = false;
    currentAttemptRef.current = 1;
    isCheckingSyncRef.current = false;
    clearPollingInterval();

    // Delegate to the new parameterized function
    await checkSyncAndFetchDataForTickers(tickerList);
  };

  // Initial data fetch and refetch when tickerList changes
  // Only fetch data for tickers that don't already have data loaded
  useEffect(() => {
    if (tickerList.length === 0) return;
    
    console.log('ListTechnicals: useEffect triggered with tickerList:', tickerList);
    
    // Check which tickers already have data loaded
    const tickersNeedingData = tickerList.filter(symbol => {
      const existingData = tickerTechnicalData[symbol];
      // Check if ticker has no data
      return !existingData || !existingData.price;
    });
    
    console.log('ListTechnicals: Tickers needing data:', tickersNeedingData);
    console.log('ListTechnicals: Tickers with existing data:', tickerList.filter(symbol => !tickersNeedingData.includes(symbol)));
    
    // If no tickers need data, skip the API calls
    if (tickersNeedingData.length === 0) {
      console.log('ListTechnicals: All tickers already have data, skipping API calls');
      return;
    }
    
    // Only reset polling state if we don't already have an active polling interval
    // This prevents interfering with polling started by mergeSessionStorageTickers or restoreSyncingState
    if (!syncPollingIntervalRef.current) {
      pollingStoppedRef.current = false;
      currentAttemptRef.current = 1;
      isCheckingSyncRef.current = false;
      clearPollingInterval();
    }
    
    setLoading(true);
    
    const checkInitialSyncStatus = async () => {
      try {
        console.log('ListTechnicals: checkInitialSyncStatus called');
        const api = isAuthenticated ? userTickerApi : tickerApi;
        const syncResponse = await api.checkTickerSyncStatus({
          ticker_symbols: tickersNeedingData,
        });
        console.log('ListTechnicals: Initial sync check response:', syncResponse);

        if (!syncResponse.success || !syncResponse.data || !syncResponse.data.tickers) {
          console.error('ListTechnicals: Invalid sync response structure:', syncResponse);
          setError('Invalid response from sync status check');
          return;
        }

        // Calculate last updated from latest last_sync_ended_at of synced tickers
        const syncedTickersWithDate = syncResponse.data.tickers.filter((t: any) => t.is_synced === true && t.last_sync_ended_at);
        if (syncedTickersWithDate.length > 0) {
          const lastSyncDates = syncedTickersWithDate
            .map((t: any) => {
              const date = typeof t.last_sync_ended_at === 'string' 
                ? new Date(t.last_sync_ended_at) 
                : t.last_sync_ended_at;
              return date instanceof Date && !isNaN(date.getTime()) ? date : null;
            })
            .filter((d: Date | null): d is Date => d !== null);
          
          if (lastSyncDates.length > 0) {
            const latestDate = new Date(Math.max(...lastSyncDates.map((d: Date) => d.getTime())));
            setLastUpdated(latestDate);
          }
        }

        // Store ticker names from sync response
        const newTickerNames: { [symbol: string]: string } = {};
        syncResponse.data.tickers.forEach((ticker: any) => {
          if (ticker.ticker_name) {
            newTickerNames[ticker.ticker_symbol] = ticker.ticker_name;
          }
        });
        setTickerNames(prev => ({ ...prev, ...newTickerNames }));

        const syncedTickers = syncResponse.data.tickers
          .filter((ticker: any) => ticker.is_synced === true)
          .map((ticker: any) => ticker.ticker_symbol);
        
        const unsyncedTickers = syncResponse.data.tickers
          .filter((ticker: any) => ticker.is_synced === false)
          .map((ticker: any) => ticker.ticker_symbol);

        console.log('ListTechnicals: syncedTickers:', syncedTickers);
        console.log('ListTechnicals: unsyncedTickers:', unsyncedTickers);

        // Update unsynced tickers state (add to existing set, don't replace)
        setUnsyncedTickers(prev => {
          const newSet = new Set(prev);
          unsyncedTickers.forEach((ticker: string) => newSet.add(ticker));
          // Remove newly synced tickers from unsynced set
          syncedTickers.forEach((ticker: string) => newSet.delete(ticker));
          return newSet;
        });

        // Fetch data for synced tickers that need data immediately
        const syncedTickersNeedingData = syncedTickers.filter((symbol: string) => tickersNeedingData.includes(symbol));
        if (syncedTickersNeedingData.length > 0) {
          console.log(`ListTechnicals: Fetching data for synced tickers that need data: ${syncedTickersNeedingData.join(', ')}`);
          const technicalData = await fetchAllTechnicalData(syncResponse.data.tickers.filter((ticker: any) => ticker.is_synced && tickersNeedingData.includes(ticker.ticker_symbol)));
          await fetchPriceData(syncedTickersNeedingData, technicalData);
    // Safety: if we have data, this ticker should no longer show as syncing/unsynced.
    setSyncingTickers(prev => {
      const next = new Set(prev);
      syncedTickersNeedingData.forEach((t: string) => next.delete(t));
      return next;
    });
    setUnsyncedTickers(prev => {
      const next = new Set(prev);
      syncedTickersNeedingData.forEach((t: string) => next.delete(t));
      return next;
    });
          // lastUpdated is already set from last_sync_ended_at above, don't override it
          console.log(`ListTechnicals: Successfully fetched data for ${syncedTickersNeedingData.join(', ')}`);
        } else {
          console.log('ListTechnicals: All synced tickers already have data, skipping data fetch');
        }

        // If there are unsynced tickers that need data, start polling for them immediately
        const unsyncedTickersNeedingData = unsyncedTickers.filter((symbol: string) => tickersNeedingData.includes(symbol));
        if (unsyncedTickersNeedingData.length > 0) {
          console.log(`ListTechnicals: Found unsynced tickers that need data: ${unsyncedTickersNeedingData.join(', ')}`);
          console.log('ListTechnicals: Starting immediate check for unsynced tickers that need data');
          
          // Only start polling if we don't already have an active polling interval
          if (!syncPollingIntervalRef.current) {
            // Reset polling state when starting new polling
            pollingStoppedRef.current = false;
            currentAttemptRef.current = 1; // Reset attempt counter
            isCheckingSyncRef.current = false; // Reset concurrent check flag
            
            setTimeout(() => {
              console.log('ListTechnicals: Executing immediate check for unsynced tickers');
              checkSyncAndFetchDataForTickers(unsyncedTickersNeedingData, 1);
            }, 1000); // 1 second delay to avoid rate limiting
          } else {
            console.log('ListTechnicals: Polling already active, skipping new polling start');
          }
        }

        setError(null);
      } catch (error) {
        console.error('Error in initial sync check:', error);
        const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
        
        // Check if it's a rate limit error (429)
        if (errorMessage.includes('429') || errorMessage.includes('rate limit')) {
          handleRateLimitError();
        } else {
          setError(`Failed to check initial sync status: ${errorMessage}`);
        }
      }
    };

    checkInitialSyncStatus().finally(() => setLoading(false));
  }, [tickerList]); // Fetch when tickerList changes (new tickers added/removed)

  // Cleanup polling interval on unmount
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, []);

  // Register current state with the global session save service
  useEffect(() => {
    if (!isAuthenticated) {
      sessionSaveService.registerSessionData({
        tickerList,
        listName,
        hiddenStatistics: {
          technicals: hiddenStatistics
        }
      });
    }
  }, [tickerList, listName, hiddenStatistics, isAuthenticated]);

  // Persist syncingTickers to sessionStorage whenever it changes
  // Use list ID-specific key for authenticated users
  // Also migrate from global key to list ID-specific key if currentListId becomes available
  useEffect(() => {
    if (!isAuthenticated || currentListId === null) {
      // For unauthenticated users, use global key
      if (syncingTickers.size > 0) {
        sessionStorage.setItem('stocktable-syncing-tickers', JSON.stringify(Array.from(syncingTickers)));
      } else {
        sessionStorage.removeItem('stocktable-syncing-tickers');
      }
      return;
    }
    
    // For authenticated users, use list ID-specific key
    const key = getSyncingTickersKey();
    
    // Migrate from global key to list ID-specific key on first use
    const globalKey = 'stocktable-syncing-tickers';
    const globalSaved = sessionStorage.getItem(globalKey);
    if (globalSaved && syncingTickers.size === 0) {
      // If we have syncing tickers in global key but not in state, restore them
      try {
        const globalTickers = JSON.parse(globalSaved);
        setSyncingTickers(new Set(globalTickers));
        // Clear global key after migration
        sessionStorage.removeItem(globalKey);
      } catch (error) {
        console.error('Error migrating syncing tickers:', error);
      }
    }
    
    if (syncingTickers.size > 0) {
      sessionStorage.setItem(key, JSON.stringify(Array.from(syncingTickers)));
    } else {
      sessionStorage.removeItem(key);
    }
  }, [syncingTickers, getSyncingTickersKey, isAuthenticated, currentListId]);

  // Restore syncing state for tickers that were syncing before refresh (only on initial load)
  useEffect(() => {
    // Only run once on initial load, not when new tickers are added during the session
    if (hasRestoredSyncingStateRef.current) return;
    
    // Wait for tickerList to be loaded
    if (tickerList.length === 0) return;
    
    const restoreSyncingState = async () => {
      // Double-check flag to prevent race conditions
      if (hasRestoredSyncingStateRef.current) return;
      
      // Get tickers that were syncing from sessionStorage and are in current list
      const syncingArray = Array.from(syncingTickers).filter(ticker => tickerList.includes(ticker));
      if (syncingArray.length === 0) {
        // Mark as restored even if no tickers to restore
        hasRestoredSyncingStateRef.current = true;
        return;
      }
      
      // Mark as restored before making the API call to prevent duplicate calls
      hasRestoredSyncingStateRef.current = true;
      
      try {
        console.log(`🔄 Restoring sync status for ${syncingArray.length} tickers that were syncing before refresh:`, syncingArray);
        const api = isAuthenticated ? userTickerApi : tickerApi;
        const syncResponse = await api.checkTickerSyncStatus({
          ticker_symbols: syncingArray,
        });
        
        const syncedTickers: string[] = [];
        const stillSyncingTickers: string[] = [];
        
        syncResponse.data?.tickers?.forEach((t: any) => {
          if (t.is_synced) {
            syncedTickers.push(t.ticker_symbol);
          } else {
            stillSyncingTickers.push(t.ticker_symbol);
          }
        });
        
        // Remove synced tickers from syncing set and unsynced set, then fetch their data
        if (syncedTickers.length > 0) {
          setSyncingTickers(prev => {
            const newSet = new Set(prev);
            syncedTickers.forEach(ticker => newSet.delete(ticker));
            return newSet;
          });
          // Also remove from unsyncedTickers to ensure they're not shown as loading
          setUnsyncedTickers(prev => {
            const newSet = new Set(prev);
            syncedTickers.forEach(ticker => newSet.delete(ticker));
            return newSet;
          });
          console.log(`✅ ${syncedTickers.length} tickers finished syncing:`, syncedTickers);
          
          // Fetch data for synced tickers
          const syncedTickerStatuses = syncResponse.data?.tickers?.filter((t: any) => 
            syncedTickers.includes(t.ticker_symbol)
          ) || [];
          
          if (syncedTickerStatuses.length > 0) {
            console.log(`🔄 Fetching data for ${syncedTickers.length} restored synced tickers:`, syncedTickers);
            setTimeout(async () => {
              try {
                // Fetch technical data (this updates state internally)
                const technicalData = await fetchAllTechnicalData(syncedTickerStatuses);
                await new Promise(resolve => setTimeout(resolve, 100));
                // Fetch price data (this also updates state)
                await fetchPriceData(syncedTickers, technicalData);
                // Safety: ensure restored tickers don't remain in syncing pulse after data is fetched.
                setSyncingTickers(prev => {
                  const next = new Set(prev);
                  syncedTickers.forEach(t => next.delete(t));
                  return next;
                });
                setUnsyncedTickers(prev => {
                  const next = new Set(prev);
                  syncedTickers.forEach(t => next.delete(t));
                  return next;
                });
                // lastUpdated is already set from last_sync_ended_at above, don't override it
                
                // Save synced tickers to database if authenticated
                if (isAuthenticated && currentListId !== null && syncedTickers.length > 0) {
                  const mergedList = Array.from(new Set([...syncedTickers, ...tickerList]));
                  
                  // Only save if the merged list is actually different from current tickerList
                  // This prevents unnecessary API calls when tickers are already in the list
                  const currentListSet = new Set(tickerList);
                  const isDifferent = mergedList.length !== tickerList.length || 
                                     !syncedTickers.every((ticker: string) => currentListSet.has(ticker));
                  
                  if (isDifferent) {
                    await autoSaveList(mergedList);
                    console.log(`💾 Saved ${syncedTickers.length} restored synced tickers to database:`, syncedTickers);
                  } else {
                    console.log(`ℹ️ No changes needed - tickers are already in the list`);
                  }
                }
                
                console.log(`✅ Successfully fetched data for restored synced tickers:`, syncedTickers);
      } catch (error) {
                console.error('❌ Error fetching data for restored synced tickers:', error);
              }
            }, 300); // Small delay to avoid rate limiting
          }
        }
        
        // Keep still-syncing tickers in the set and add them to unsyncedTickers to resume polling
        if (stillSyncingTickers.length > 0) {
          console.log(`⏳ ${stillSyncingTickers.length} tickers still syncing, resuming polling:`, stillSyncingTickers);
          
          // Add to unsyncedTickers to trigger polling (don't reset polling state if polling is already active)
          setUnsyncedTickers(prev => {
            const newSet = new Set(prev);
            stillSyncingTickers.forEach(ticker => newSet.add(ticker));
            return newSet;
          });
          
          // Only start polling if it's not already active (mergeSessionStorageTickers may have already started it)
          if (!syncPollingIntervalRef.current) {
            // Reset polling state to ensure polling can resume
            pollingStoppedRef.current = false;
            currentAttemptRef.current = 1;
            isCheckingSyncRef.current = false;
            
            // Start polling for these tickers
            setTimeout(() => {
              console.log('🔄 Starting polling for restored syncing tickers:', stillSyncingTickers);
              checkSyncAndFetchDataForTickers(stillSyncingTickers, 1);
            }, 1000); // 1 second delay to avoid rate limiting
          } else {
            console.log('🔄 Polling already active from mergeSessionStorageTickers, unsyncedTickers updated, polling will continue');
          }
        }
      } catch (error) {
        console.error('❌ Error checking sync status for syncing tickers:', error);
      }
    };

    // Run once after tickerList is loaded (with a delay to ensure it's fully loaded)
    const timeoutId = setTimeout(restoreSyncingState, 500);
    return () => clearTimeout(timeoutId);
  }, [tickerList, syncingTickers, isAuthenticated, currentListId, setUnsyncedTickers, checkSyncAndFetchDataForTickers, autoSaveList]); // Only run when tickerList is first loaded

  // Recovery: Merge tickers from sessionStorage into tickerList immediately (for authenticated users)
  // This ensures tickers that were added but not yet in database appear immediately with loading state
  useEffect(() => {
    const mergeSessionStorageTickers = () => {
      if (!isAuthenticated || currentListId === null) return;
      
      // Check sessionStorage for tickers that might not be in database yet
      // Use list ID-specific key to only merge tickers for the current list
      const savedTickers = sessionStorage.getItem(getTickerListKey());
      if (!savedTickers) return;
      
      try {
        const sessionTickers: string[] = JSON.parse(savedTickers);
        // Deduplicate sessionStorage tickers first
        const uniqueSessionTickers = Array.from(new Set(sessionTickers));
        // Deduplicate current tickerList
        const uniqueTickerList = Array.from(new Set(tickerList));
        // Find tickers that are in sessionStorage but not in current list
        const missingTickers = uniqueSessionTickers.filter(ticker => !uniqueTickerList.includes(ticker));
        
        if (missingTickers.length === 0) {
          // If no missing tickers but sessionStorage has duplicates, clean it up
          if (uniqueSessionTickers.length !== sessionTickers.length) {
            sessionStorage.setItem(getTickerListKey(), JSON.stringify(uniqueSessionTickers));
            console.log(`🔧 Cleaned up duplicates in sessionStorage: ${sessionTickers.length} -> ${uniqueSessionTickers.length}`);
          }
          return;
        }
        
        console.log(`🔄 Found ${missingTickers.length} tickers in sessionStorage not in database list:`, missingTickers);
        
        // Preserve order from sessionStorage: merge by keeping sessionStorage order and inserting missing tickers
        // at their original positions, then appending any remaining tickers from database list
        const mergedList: string[] = [];
        
        // First, add all tickers from sessionStorage in their original order
        for (const ticker of uniqueSessionTickers) {
          if (!mergedList.includes(ticker)) {
            mergedList.push(ticker);
          }
        }
        
        // Then, add any tickers from database list that weren't in sessionStorage
        for (const ticker of uniqueTickerList) {
          if (!mergedList.includes(ticker)) {
            mergedList.push(ticker);
          }
        }
        
        setTickerList(mergedList);
        // Also save the deduplicated merged list back to sessionStorage
        sessionStorage.setItem(getTickerListKey(), JSON.stringify(mergedList));
        console.log(`✅ Merged ${missingTickers.length} tickers from sessionStorage into list, preserving order`);
        
        // Also restore syncing state for these tickers if they were syncing
        // Use list ID-specific key to only restore syncing state for the current list
        const savedSyncing = sessionStorage.getItem(getSyncingTickersKey());
        if (savedSyncing) {
          const syncingArray: string[] = JSON.parse(savedSyncing);
          const syncingTickersToRestore = syncingArray.filter(ticker => missingTickers.includes(ticker));
          if (syncingTickersToRestore.length > 0) {
            setSyncingTickers(prev => {
              const newSet = new Set(prev);
              syncingTickersToRestore.forEach(ticker => newSet.add(ticker));
              return newSet;
            });
            // Also add to unsyncedTickers so polling can resume
            setUnsyncedTickers(prev => {
              const newSet = new Set(prev);
              syncingTickersToRestore.forEach(ticker => newSet.add(ticker));
              return newSet;
            });
            console.log(`✅ Restored syncing state for ${syncingTickersToRestore.length} tickers:`, syncingTickersToRestore);
            
            // Start polling for these tickers immediately
            // Reset polling state to ensure polling can resume
            pollingStoppedRef.current = false;
            currentAttemptRef.current = 1;
            isCheckingSyncRef.current = false;
            
            // Start polling for these tickers
            setTimeout(() => {
              console.log('🔄 Starting polling for merged syncing tickers:', syncingTickersToRestore);
              checkSyncAndFetchDataForTickers(syncingTickersToRestore, 1);
            }, 1000); // 1 second delay to avoid rate limiting
          }
        }
    } catch (error) {
        console.error('❌ Error merging sessionStorage tickers:', error);
      }
    };

    // Run after tickerList is loaded from database (with a small delay to ensure database list is loaded)
    const timeoutId = setTimeout(mergeSessionStorageTickers, 100);
    return () => clearTimeout(timeoutId);
  }, [isAuthenticated, currentListId, tickerList, setTickerList, setSyncingTickers, setUnsyncedTickers, getTickerListKey, getSyncingTickersKey, checkSyncAndFetchDataForTickers]);

  // Recovery: Check for tickers in sessionStorage that need to be synced to database
  useEffect(() => {
    // Only run once per list load to prevent unnecessary API calls
    if (hasRecoveredPendingTickersRef.current) return;
    
    const recoverPendingTickers = async () => {
      if (!isAuthenticated || !currentListId || tickerList.length === 0) return;
      
      // Check sessionStorage for tickers that might not be in database yet
      // Use list ID-specific key to only recover tickers for the current list
      const savedTickers = sessionStorage.getItem(getTickerListKey());
      if (!savedTickers) {
        hasRecoveredPendingTickersRef.current = true;
      return;
    }
    
      try {
        const sessionTickers: string[] = JSON.parse(savedTickers);
        const missingTickers = sessionTickers.filter(ticker => !tickerList.includes(ticker));
        
        if (missingTickers.length === 0) {
          // No missing tickers - mark as recovered and clear sessionStorage if everything is already saved
          hasRecoveredPendingTickersRef.current = true;
          // Clear sessionStorage since everything is already in the database
          sessionStorage.removeItem(getTickerListKey());
          return;
        }
        
        console.log(`🔄 Checking sync status for ${missingTickers.length} tickers in sessionStorage:`, missingTickers);
        
        // Check if these tickers have been synced (exist in tickerBasics)
        const api = userTickerApi;
        const syncResponse = await api.checkTickerSyncStatus({
          ticker_symbols: missingTickers,
        });
        
        const syncedTickers = syncResponse.data?.tickers
          ?.filter((t: any) => t.is_synced === true)
          .map((t: any) => t.ticker_symbol) || [];
        
        if (syncedTickers.length === 0) {
          console.log('⚠️ No missing tickers are synced yet, will retry later');
          // Don't mark as recovered yet - we'll retry later
          return;
        }
        
        // Mark as recovered since we're processing the recovery now
        hasRecoveredPendingTickersRef.current = true;
        
        console.log(`✅ Found ${syncedTickers.length} synced tickers that need to be saved:`, syncedTickers);
        
        // Merge synced tickers with current list and save to database
        const mergedList = Array.from(new Set([...syncedTickers, ...tickerList]));
        
        // Only save if the merged list is actually different from current tickerList
        // This prevents unnecessary API calls on page refresh when nothing has changed
        const currentListSet = new Set(tickerList);
        const isDifferent = mergedList.length !== tickerList.length || 
                           !syncedTickers.every((ticker: string) => currentListSet.has(ticker));
        
        if (isDifferent) {
          await autoSaveList(mergedList);
          console.log(`💾 Recovered and saved ${syncedTickers.length} tickers to database`);
          // Update local state
          setTickerList(mergedList);
          // Clear sessionStorage after successful save
          sessionStorage.removeItem(getTickerListKey());
        } else {
          console.log(`ℹ️ No changes needed - all tickers are already in the list`);
          // Clear sessionStorage since everything is already saved
          sessionStorage.removeItem(getTickerListKey());
      }
    } catch (error) {
        console.error('❌ Error recovering pending tickers:', error);
        // Mark as recovered even on error to prevent infinite retries
        hasRecoveredPendingTickersRef.current = true;
      }
    };

    // Only run recovery once after initial load, with a delay to ensure database list is loaded
    const timeoutId = setTimeout(recoverPendingTickers, 1500);
    return () => clearTimeout(timeoutId);
  }, [isAuthenticated, currentListId, tickerList, autoSaveList, setTickerList, getTickerListKey]);
  
  // Reset recovery flag when list ID changes
  useEffect(() => {
    hasRecoveredPendingTickersRef.current = false;
  }, [currentListId]);

  // Ticker management functions using useAddTicker hook
  const { handleAddTicker } = useAddTicker({
    isAuthenticated,
    tickerList,
    setTickerList,
    setSyncingTickers,
    tempTickerList: tempTickerList || [],
    setTempTickerList: setTempTickerList as React.Dispatch<React.SetStateAction<string[]>> | undefined,
    isEditingTickers,
    initializeEmptyData: useCallback((ticker: string) => {
      setTickerTechnicalData(prev => ({
        ...prev,
        [ticker]: {
          technicals: {},
          price: null,
          movingAverages: [],
        }
      }));
    }, []),
    onTickerNameUpdate: useCallback((ticker: string, tickerName: string) => {
      setTickerNames(prev => ({
        ...prev,
        [ticker]: tickerName
      }));
    }, []),
    onSyncComplete: useCallback(async (ticker: string, tickerStatus: any) => {
      // Ticker is synced, fetch its data
      const technicalData = await fetchAllTechnicalData([tickerStatus]);
      console.log(`ListTechnicals: Fetched technical data for ${ticker}:`, technicalData);
      await new Promise(resolve => setTimeout(resolve, 100));
      await fetchPriceData([ticker], technicalData);
      console.log(`ListTechnicals: Fetched price data for ${ticker}`);
      // Note: lastUpdated should be set from last_sync_ended_at in checkSyncAndFetchDataForTickers
    }, [fetchAllTechnicalData, fetchPriceData]),
    autoSaveList,
    saveListImmediately: useCallback(async (tickers: string[]) => {
      const uniqueTickers = Array.from(new Set(tickers));
      if (isPublicList) return;

      if (isAuthenticated && !isPublicList) {
        let listId = currentListId;
        if (listId === null) {
          const cachedListId = getCachedCurrentListId();
          if (cachedListId) listId = cachedListId;
          else if (userLists.length > 0) {
            try {
              const defaultListResponse = await userApi.getDefaultList();
              if (defaultListResponse.defaultUserTickerListId && userLists.some((l: UserTickerList) => l.id === defaultListResponse.defaultUserTickerListId)) {
                listId = defaultListResponse.defaultUserTickerListId;
              } else {
                listId = userLists[0].id;
              }
            } catch {
              listId = userLists[0].id;
            }
          }
        }
        if (listId != null) {
          try {
            const tickerMappings = uniqueTickers.map((ticker, index) => ({ ticker_symbol: ticker, order_num: index + 1 }));
            await userTickerListApi.updateTickerMappings(listId, tickerMappings);
            clearListCache(listId);
            needsListDataReload.current = true;
          } catch (error) {
            console.error('saveListImmediately: failed to save to server', error);
          }
        }
        return;
      }

      sessionStorage.setItem(getTickerListKey(), JSON.stringify(uniqueTickers));
    }, [getTickerListKey, currentListId, isPublicList, isAuthenticated, userLists]),
    pollingInterval: 1000,
    maxPollAttempts: 30,
    normalizeTicker: (ticker: string) => ticker, // Keep original case for ListTechnicals
  });

  const handleRemoveTicker = async (tickerToRemove: string) => {
    if (isEditingTickers) {
      const newTempList = tempTickerList.filter(t => t !== tickerToRemove);
      setTempTickerList(newTempList);
      // No auto-save in edit mode - will be saved when "Done" is clicked
        } else {
      const newList = tickerList.filter(t => t !== tickerToRemove);
      setTickerList(newList);
      
      // Auto-save for authenticated users (only when not in edit mode)
      if (isAuthenticated) {
        await autoSaveList(newList);
      }
    }
  };


  // Hidden statistics handlers

  // Category info modal handlers
  const handleCategoryInfoClick = (categoryName: string) => {
    setSelectedCategory(categoryName);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedCategory('');
  };



  const handleRefetch = (tickerSymbol: string) => {
    if (isPublicList && publicUuid) {
      navigate(`/ticker/${tickerSymbol}/technicals?public_uuid=${publicUuid}`);
    } else {
      navigate(`/ticker/${tickerSymbol}/technicals`);
    }
  };

  // Handle refresh usage limits callback
  const handleRefreshUsageLimits = (refreshFn: () => void) => {
    refreshUsageLimitsRef.current = refreshFn;
  };

  // Share modal handlers
  const handleShareList = () => {
    setIsShareModalOpen(true);
  };

  const handleShareModalClose = () => {
    setIsShareModalOpen(false);
  };

  const handleShareModalUpdate = async () => {
    // Refresh user lists to get updated share settings
    if (isAuthenticated && currentListId) {
      try {
        const updatedLists = await userTickerListApi.getUserLists();
        setUserLists(updatedLists);
        const updatedList = updatedLists.find(list => list.id === currentListId);
        if (updatedList) {
          cacheUserLists(updatedLists);
        }
      } catch (error) {
        console.error('Failed to refresh lists after share update:', error);
      }
    }
  };

  // Get current list data for share modal
  const currentList = currentListId && userLists
    ? userLists.find(list => list.id === currentListId) || null
    : null;

  // Modals hook (must be after handleListChange is defined)
  const {
    isDeleteModalOpen,
    isErrorDialogOpen,
    errorMessage,
    handleDeleteList,
    handleDeleteCancel,
    handleDeleteConfirm,
    handleErrorDialogClose,
  } = useModals({
    currentListId,
    isAuthenticated,
    userLists,
    needsUserListsReload,
    handleListChange,
    refreshUsageLimitsRef,
    onAfterDelete: () => {
      setCurrentListId(null);
      setListName('Main List');
      setTickerList([]);
      setHiddenStatistics([]);
    },
  });

  // Tabs component
  const tabs = (
    <TabBar 
      activeTab="technicals" 
      disabled={isEditingTickers} 
      publicUuid={publicUuid}
    />
  );

  // Stock Table now calculates category headers dynamically (like TickerTechnicals)
  // So we just pass the base category headers with original startIndices
  // Stock Table will calculate which headers to show based on current hiddenStatistics
  const currentCategoryHeaders = CATEGORY_HEADERS;

  // If public list is not found, show simple centered message
  // Also check publicUuid directly to ensure we exit this state when URL changes
  if (publicUuid && isPublicListNotFound) {
    return (
      <>
        <div className="flex flex-col min-h-screen">
          <Header />
          {!isAuthenticated && <PublicListPromoBar />}
          <div className="flex-grow flex items-center justify-center">
            <div className="text-center">
              <p className="text-lg text-gray-700 dark:text-text-primary">
                {publicListError || 'This list does not exist or is no longer available.'}
              </p>
            </div>
          </div>
          <div className="mt-4 sm:mt-8">
            <Footer />
          </div>
        </div>
      </>
    );
  }

  return (
    <>
    <div className="flex flex-col min-h-screen">
      <StockTableLayout
        tickerList={displayTickerList}
        tickerData={Object.keys(tickerTechnicalData).map(symbol => getTickerData(symbol)).filter(Boolean) as TickerDisplayData[]}
        loading={loading || isLoadingPublicList}
        error={error || (isPublicList ? publicListError : null)}
        lastUpdated={lastUpdated}
        isEditingTickers={isPublicList ? false : isEditingTickers}
        isPublicList={isPublicList}
        showPublicListPromoBar={isPublicList && !isAuthenticated}
        hiddenStatistics={hiddenStatistics}
        draggedTicker={draggedTicker}
        dragOverTicker={dragOverTicker}
        droppedTicker={droppedTicker}
        statistics={TECHNICAL_STATISTICS}
        categoryHeaders={currentCategoryHeaders}
        getTickerData={getTickerData}
        formatStatisticValue={formatStatisticValue}
        getStatisticColor={getStatisticColor}
        lockedStatistics={lockedStatistics}
        proOnlyStatistics={PRO_ONLY_STATISTICS}
        lockedTickers={lockedTickers}
        onRefetch={() => {
          setLoading(true);
          checkSyncAndFetchData().finally(() => setLoading(false));
        }}
        onTickerClick={handleRefetch}
        onRemoveTicker={isPublicList ? () => {} : handleRemoveTicker}
        onDragStart={isPublicList ? () => {} : handleDragStart}
        onDragOver={isPublicList ? () => {} : handleDragOver}
        onDragLeave={isPublicList ? () => {} : handleDragLeave}
        onDrop={isPublicList ? () => {} : handleDrop}
        onDragEnd={isPublicList ? () => {} : handleDragEnd}
        onHideStatistic={isPublicList ? () => {} : handleHideStatistic}
        onShowStatistic={isPublicList ? () => {} : handleShowStatistic}
        onCategoryInfoClick={handleCategoryInfoClick}
        listName={listName}
        onStartEdit={isPublicList ? () => {} : handleStartEdit}
        onCancelEdit={isPublicList ? () => {} : handleCancelEdit}
        onDoneEdit={isPublicList ? () => {} : handleDoneEdit}
        onAddTicker={isPublicList ? () => {} : handleAddTicker}
        onSave={handleSave}
        tempListName={tempListName}
        onTempListNameChange={setTempListName}
        userLists={isPublicList ? [] : userLists}
        currentListId={isPublicList ? undefined : (currentListId || undefined)}
        onListChange={isPublicList ? undefined : handleListChange}
        onCreateNewList={isPublicList ? undefined : handleCreateNewList}
        onCreateNewListWithName={isPublicList ? undefined : handleCreateNewListWithName}
        onDeleteList={handleDeleteList}
        showDeleteButton={!isPublicList && isAuthenticated && userLists.length > 1}
        onShareList={handleShareList}
        showShareButton={!isPublicList && isAuthenticated && currentListId !== null && !isCurrentListLocked}
        onRefreshUsageLimits={handleRefreshUsageLimits}
        tabs={tabs}
        showRefreshButton={false}
        showDelayedPricesText={false}
        showLastUpdatedDate={true}
        statisticsColumnWidthMultiplier={0.85}
        fullScreenStatisticsColumnWidthMultiplier={0.85}
        useListDailySmallScreenColumnWidth
      />
      
      {/* Footer */}
      <div className="mt-4 sm:mt-8">
        <Footer />
      </div>
    </div>
      
      {/* Category Info Modal */}
      <CategoryInfoModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        categoryName={selectedCategory}
      />
      
      {/* Delete Confirmation Modal */}
      <DeleteListConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        listName={listName}
      />
      
      {/* Error Dialog */}
      <ErrorDialog
        isOpen={isErrorDialogOpen}
        onClose={handleErrorDialogClose}
        title="Error"
        message={errorMessage}
      />
      
      {/* Share List Modal */}
      <ShareListModal
        isOpen={isShareModalOpen}
        onClose={handleShareModalClose}
        listId={currentListId}
        currentList={currentList}
        onUpdate={handleShareModalUpdate}
        isListLocked={isCurrentListLocked}
      />
    </>
  );
};

export default ListTechnicals;
